import { useState, useEffect, useCallback } from 'react';
import {
  Box, Typography, Paper, Button, TextField, MenuItem, Select, FormControl,
  InputLabel, Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  IconButton, Chip, Dialog, DialogTitle, DialogContent, DialogActions,
  CircularProgress, Alert, Tooltip,
} from '@mui/material';
import {
  Block as BlockIcon, Delete as DeleteIcon, Add as AddIcon,
  Email as EmailIcon, Wifi as IpIcon,
} from '@mui/icons-material';
import { useAuth } from '@hooks/useAuth';
import { getBlockedEntities, addBlockedEntity, removeBlockedEntity } from '@services/blocklistService';

export default function BlockedEntitiesPage() {
  const { currentUser, userData } = useAuth();
  const [entities, setEntities] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [saving, setSaving] = useState(false);

  const [form, setForm] = useState({ type: 'email', value: '', reason: '' });

  const load = useCallback(async () => {
    setLoading(true);
    try {
      setEntities(await getBlockedEntities());
    } catch (e) {
      setError('Failed to load block list.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const handleAdd = async () => {
    if (!form.value.trim()) return;
    setSaving(true);
    try {
      await addBlockedEntity({
        type: form.type,
        value: form.value,
        reason: form.reason,
        actorId: currentUser.uid,
        actorName: userData?.displayName || currentUser.email,
      });
      setDialogOpen(false);
      setForm({ type: 'email', value: '', reason: '' });
      await load();
    } catch (e) {
      setError('Failed to add entry.');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;
    setSaving(true);
    try {
      await removeBlockedEntity(deleteTarget.id);
      setDeleteTarget(null);
      await load();
    } catch (e) {
      setError('Failed to remove entry.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Box sx={{ p: 3, maxWidth: 900, mx: 'auto' }}>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <BlockIcon color="error" />
          <Typography variant="h5" fontWeight="bold">Block List</Typography>
        </Box>
        <Button variant="contained" startIcon={<AddIcon />} onClick={() => setDialogOpen(true)}>
          Add Entry
        </Button>
      </Box>

      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>{error}</Alert>}

      <TableContainer component={Paper} variant="outlined">
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Type</TableCell>
              <TableCell>Value</TableCell>
              <TableCell>Reason</TableCell>
              <TableCell>Added By</TableCell>
              <TableCell>Date</TableCell>
              <TableCell align="right">Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loading ? (
              <TableRow>
                <TableCell colSpan={6} align="center" sx={{ py: 4 }}>
                  <CircularProgress size={24} />
                </TableCell>
              </TableRow>
            ) : entities.length === 0 ? (
              <TableRow>
                <TableCell colSpan={6} align="center" sx={{ py: 4, color: 'text.secondary' }}>
                  No blocked entries.
                </TableCell>
              </TableRow>
            ) : entities.map((e) => (
              <TableRow key={e.id} hover>
                <TableCell>
                  <Chip
                    icon={e.type === 'email' ? <EmailIcon /> : <IpIcon />}
                    label={e.type}
                    size="small"
                    color={e.type === 'email' ? 'warning' : 'error'}
                    variant="outlined"
                  />
                </TableCell>
                <TableCell sx={{ fontFamily: 'monospace' }}>{e.value}</TableCell>
                <TableCell>{e.reason || '—'}</TableCell>
                <TableCell>{e.createdByName || '—'}</TableCell>
                <TableCell>
                  {e.createdAt?.toDate
                    ? e.createdAt.toDate().toLocaleDateString()
                    : '—'}
                </TableCell>
                <TableCell align="right">
                  <Tooltip title="Remove block">
                    <IconButton size="small" color="error" onClick={() => setDeleteTarget(e)}>
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Add dialog */}
      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Add Block Entry</DialogTitle>
        <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 2 }}>
          <FormControl fullWidth size="small">
            <InputLabel>Type</InputLabel>
            <Select
              value={form.type}
              label="Type"
              onChange={(e) => setForm((f) => ({ ...f, type: e.target.value }))}
            >
              <MenuItem value="email">Email</MenuItem>
              <MenuItem value="ip">IP Address</MenuItem>
            </Select>
          </FormControl>
          <TextField
            label={form.type === 'email' ? 'Email address' : 'IP address'}
            value={form.value}
            onChange={(e) => setForm((f) => ({ ...f, value: e.target.value }))}
            size="small"
            fullWidth
            placeholder={form.type === 'email' ? 'user@example.com' : '192.168.1.1'}
          />
          <TextField
            label="Reason (optional)"
            value={form.reason}
            onChange={(e) => setForm((f) => ({ ...f, reason: e.target.value }))}
            size="small"
            fullWidth
            multiline
            rows={2}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogOpen(false)}>Cancel</Button>
          <Button variant="contained" color="error" onClick={handleAdd} disabled={saving || !form.value.trim()}>
            {saving ? <CircularProgress size={18} /> : 'Block'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Confirm delete dialog */}
      <Dialog open={!!deleteTarget} onClose={() => setDeleteTarget(null)}>
        <DialogTitle>Remove Block</DialogTitle>
        <DialogContent>
          <Typography>
            Remove block on <strong>{deleteTarget?.value}</strong>? They will be able to sign in again.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteTarget(null)}>Cancel</Button>
          <Button variant="contained" color="error" onClick={handleDelete} disabled={saving}>
            {saving ? <CircularProgress size={18} /> : 'Remove'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
